import React, { useMemo } from 'react';
import { bool, func, object, oneOf, string } from 'prop-types';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { Box, PackageCard } from '@landtrustinc/design-system';
import { get } from 'lodash';
import { parseMoneyObjString } from '../../util/currency';
import { propTypes } from '../../util/types';
import { ensureListing } from '../../util/data';
import { getPackage } from '../../util/listing';
import usePackageData from '../../hooks/usePackageData';
import usePackageBadges from '../PackageBadges/hooks/usePackageBadges';
import usePackageAvailability from '../../hooks/usePackageAvailability';
import { toastPackageFavorited, toastPackageUnfavorited } from '../FavoritePackage/FavoritePackage';
import OpeningPackageJSON from '../../assets/lottie/opening-package.json';
import { ClientOnlyPlayer } from '../Lottie/ClientOnlyLottie';

export const FETCHING_STATUS = {
  DELETE: 'delete',
  DUPLICATE: 'duplicate',
  PUBLISH: 'publish',
  UNPUBLISH: 'unpublish',
};

const HorizontalPackageCard = props => {
  const {
    listing,
    packageId,
    item,
    intl,
    onEdit,
    onDuplicate,
    onRemove,
    onTogglePublish,
    onClick,
    onToggleFavoritePackage,
    followSubjectMutation,
    currentUser,
    isPackageFetching,
    fetchingStatus,
  } = props;

  const currentListing = ensureListing(listing);

  // Get package data
  const currentPackage = getPackage(currentListing, packageId);
  const packageInfo = currentPackage || item;

  // Use custom hooks for package data, badges, and availability
  const packageData = usePackageData({ listing: currentListing, packageId, intl });
  const badges = usePackageBadges(packageInfo, null, currentListing);
  const { availabilityBadges, supportingText } = usePackageAvailability(packageInfo, intl);

  // Build actions array if in edit mode
  const actions = useMemo(() => {
    if (!onEdit && !onDuplicate && !onRemove && !onTogglePublish) return undefined;

    const actionList = [];

    if (onEdit) {
      actionList.push({
        icon: 'Pencil',
        label: intl.formatMessage({ id: 'PackageCard.menuEdit' }),
        onClick: () => onEdit(item),
      });
    }

    if (onDuplicate) {
      actionList.push({
        icon: 'Copy',
        label: intl.formatMessage({ id: 'PackageCard.menuDuplicate' }),
        onClick: () => onDuplicate(item),
      });
    }

    if (onTogglePublish) {
      const isPublished = packageInfo?.isPublished;
      actionList.push({
        icon: isPublished ? 'EyeSlash' : 'Eye',
        label: intl.formatMessage({
          id: isPublished ? 'PackageCard.menuUnpublish' : 'PackageCard.menuPublish',
        }),
        onClick: () => onTogglePublish(packageId, !isPublished),
      });
    }

    if (onRemove) {
      if (actionList.length > 0) {
        actionList.push('divider');
      }

      actionList.push({
        danger: true,
        icon: 'Trash',
        label: intl.formatMessage({ id: 'PackageCard.menuDelete' }),
        onClick: () => onRemove(item),
      });
    }

    return actionList;
  }, [onEdit, onDuplicate, onRemove, onTogglePublish, item, packageInfo, packageId, intl]);

  // Handle card click
  const handleClick = () => {
    if (onClick) {
      onClick({
        selectedPackage: {
          ...currentPackage,
          price: parseMoneyObjString(currentPackage.price),
          lodgingPrice: parseMoneyObjString(currentPackage.lodgingPrice),
        },
        packageId: currentPackage.id,
      });
    }
  };

  // Check if package is favorited
  const listingId = get(currentListing, 'id.uuid');
  const favoritePackages = get(currentUser, 'attributes.profile.publicData.favoritePackages', []);
  const isFavorited = favoritePackages.indexOf(`${listingId}:${packageInfo.id}`) > -1;

  // Handle favorite click with toast notifications
  const handleFavoriteClick = async e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!currentUser || !onToggleFavoritePackage) return;

    // Call the toggle action
    await onToggleFavoritePackage(packageInfo, followSubjectMutation);

    // Show toast notification
    if (isFavorited) {
      toastPackageUnfavorited(currentUser);
    } else {
      toastPackageFavorited(currentUser);
    }
  };

  // Show loading animation when fetching
  if (isPackageFetching) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        padding="2rem"
        border="1px solid var(--color-grey-100)"
        borderRadius="8px"
        minHeight="200px"
      >
        <ClientOnlyPlayer
          autoplay
          loop
          src={OpeningPackageJSON}
          style={{
            height: '100px',
          }}
        />
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          {fetchingStatus === FETCHING_STATUS.DUPLICATE && (
            <FormattedMessage id="PackageCard.fetchingDuplicate" />
          )}
          {fetchingStatus === FETCHING_STATUS.DELETE && (
            <FormattedMessage id="PackageCard.fetchingDelete" />
          )}
          {fetchingStatus === FETCHING_STATUS.PUBLISH && (
            <FormattedMessage id="PackageCard.fetchingPublish" />
          )}
          {fetchingStatus === FETCHING_STATUS.UNPUBLISH && (
            <FormattedMessage id="PackageCard.fetchingUnpublish" />
          )}
        </div>
      </Box>
    );
  }

  return (
    <PackageCard
      actions={actions}
      availabilityBadges={availabilityBadges}
      badges={badges}
      images={packageData?.images}
      onClick={onClick ? handleClick : undefined}
      onFavoriteClick={onToggleFavoritePackage && currentUser ? handleFavoriteClick : undefined}
      isFavorited={currentUser ? isFavorited : false}
      orientation="horizontal"
      startingPrice={packageData?.startingPrice}
      supportingText={supportingText}
      subtitle={packageData?.subtitle}
      title={packageData?.title || packageInfo?.title}
    />
  );
};

HorizontalPackageCard.defaultProps = {
  onEdit: null,
  onDuplicate: null,
  onRemove: null,
  onTogglePublish: null,
  onClick: null,
  onToggleFavoritePackage: null,
  followSubjectMutation: null,
  currentUser: null,
  isPackageFetching: false,
  fetchingStatus: null,
};

HorizontalPackageCard.propTypes = {
  listing: propTypes.listing.isRequired,
  packageId: string.isRequired,
  item: object.isRequired,
  intl: intlShape.isRequired,
  onEdit: func,
  onDuplicate: func,
  onRemove: func,
  onTogglePublish: func,
  onClick: func,
  onToggleFavoritePackage: func,
  followSubjectMutation: func,
  currentUser: propTypes.currentUser,
  isPackageFetching: bool,
  fetchingStatus: oneOf([
    FETCHING_STATUS.DUPLICATE,
    FETCHING_STATUS.DELETE,
    FETCHING_STATUS.PUBLISH,
    FETCHING_STATUS.UNPUBLISH,
  ]),
};

export { HorizontalPackageCard as HorizontalPackageCardComponent };
export default injectIntl(HorizontalPackageCard);
